import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Camera, ArrowLeft, Lightbulb, PenTool } from "lucide-react";
import { DoodleLightbulb, DoodleMagnify } from "@/components/Doodles";
import { useAuth } from "@/lib/AuthContext";
import ReactMarkdown from "react-markdown";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─── Avatars ─────────────────────────────────────────────────────
const PriyaAvatar = () => (
  <div style={{
    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
    background: "#FFF3E0", border: "1.5px dashed #E6A800",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16,
  }}>
    👧
  </div>
);

const BuntyAvatar = () => (
  <div style={{
    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
    background: "#E8F5E9", border: "1.5px dashed #66BB6A",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16,
  }}>
    🐒
  </div>
);

// ─── Typing dots ─────────────────────────────────────────────────
const TypingDots = () => (
  <div style={{
    display: "flex", gap: 4, alignItems: "center",
    border: "1.5px dashed #CCCCCC", borderRadius: 12,
    padding: "10px 14px", background: "#FAFAFA",
  }}>
    {[0, 1, 2].map(i => (
      <div key={i} style={{
        width: 6, height: 6, borderRadius: "50%",
        background: "rgba(68,138,255,0.5)",
        animation: `askPriyaBounce 1.2s infinite ${i * 0.15}s`,
      }} />
    ))}
    <style>{`@keyframes askPriyaBounce { 0%,80%,100% { transform:translateY(0) } 40% { transform:translateY(-6px) } }`}</style>
  </div>
);

// ─── Quick Action Card ───────────────────────────────────────────
const ActionCard = ({ icon, label, accentColor, onClick }) => (
  <button onClick={onClick} style={{
    background: "#FFFFFF", border: "1.5px dashed #CCCCCC",
    borderLeft: `3px solid ${accentColor}`, borderRadius: 4,
    padding: "12px 10px", display: "flex", alignItems: "center", gap: 8,
    cursor: "pointer", width: "100%", textAlign: "left",
  }}>
    <span style={{ color: accentColor, flexShrink: 0 }}>{icon}</span>
    <span style={{ fontFamily: "'Patrick Hand', cursive", fontSize: 14, color: "#1A1A2E" }}>{label}</span>
  </button>
);

// ─── Concept Check Card ─────────────────────────────────────────
const ConceptCheckCard = ({ check, onContinue }) => {
  const [selected, setSelected] = useState(null);
  const correct = check.correct?.toUpperCase();
  const isAnswered = selected !== null;
  const isCorrect = selected === correct;

  const options = [
    { key: "A", text: check.option_a },
    { key: "B", text: check.option_b },
    { key: "C", text: check.option_c },
    { key: "D", text: check.option_d },
  ].filter(o => o.text);

  const handleTap = (key) => {
    if (isAnswered) return;
    setSelected(key);

    // Auto-continue the lesson after showing feedback
    const wasCorrect = key === check.correct?.toUpperCase();
    const userPickText = check[`option_${key.toLowerCase()}`] || key;
    const correctText = check[`option_${check.correct?.toLowerCase()}`] || check.correct;

    const continuationMessage = wasCorrect
      ? `I answered "${userPickText}" and got it right. Please continue teaching me more about this topic — go deeper into the concept.`
      : `I answered "${userPickText}" but the correct answer was "${correctText}". Please re-teach this concept with a different approach so I can understand better.`;

    // Wait 1.5 seconds so user reads the celebrate/reteach text
    setTimeout(() => {
      if (onContinue) onContinue(continuationMessage, null, { hidden: true });
    }, 1500);
  };

  const bgColor = !isAnswered ? "#FAEEDA" : isCorrect ? "#EAF3DE" : "#FCEBEB";
  const borderColor = !isAnswered ? "#EF9F27" : isCorrect ? "#66BB6A" : "#FF6B6B";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ marginBottom: 8 }}
    >
      <div style={{
        background: bgColor, borderLeft: `3px solid ${borderColor}`,
        border: `1.5px dashed ${borderColor}`, borderLeftWidth: 3, borderLeftStyle: "solid",
        borderRadius: 4, padding: 14, transition: "background 0.3s",
      }}>
        <p style={{
          fontFamily: "'Patrick Hand', cursive", fontSize: 15, color: "#1A1A2E",
          margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ fontSize: 18 }}>💡</span> Did you get it?
        </p>
        <p style={{
          fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600,
          color: "#1A1A2E", margin: "0 0 12px", lineHeight: 1.5,
        }}>
          {check.question}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {options.map(({ key, text }) => {
            const isThis = selected === key;
            const isCorrectOption = key === correct;
            let optBg = "#FFFFFF";
            let optBorder = "#EF9F27";
            let optColor = "#1A1A2E";
            let icon = null;
            if (isAnswered) {
              if (isCorrectOption) {
                optBg = "#E8F5E9"; optBorder = "#66BB6A";
                icon = <span style={{ fontSize: 14 }}>✓</span>;
              } else if (isThis && !isCorrect) {
                optBg = "#FFEBEE"; optBorder = "#FF6B6B";
                icon = <span style={{ fontSize: 14 }}>✗</span>;
              } else {
                optBg = "#F5F5F5"; optBorder = "#DDD"; optColor = "#999";
              }
            }
            return (
              <button
                key={key}
                onClick={() => handleTap(key)}
                style={{
                  background: optBg, border: `1.5px solid ${optBorder}`,
                  borderRadius: 14, padding: "10px 14px", cursor: isAnswered ? "default" : "pointer",
                  textAlign: "left", fontSize: 14, fontFamily: "'Inter', sans-serif",
                  color: optColor, display: "flex", alignItems: "center", gap: 8,
                  transition: "all 0.2s",
                }}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  border: `1.5px solid ${optBorder}`, display: "flex",
                  alignItems: "center", justifyContent: "center", fontSize: 12,
                  fontWeight: 700, background: isAnswered && isCorrectOption ? "#C8E6C9" : "transparent",
                }}>
                  {icon || key}
                </span>
                {text}
              </button>
            );
          })}
        </div>
      </div>

      {/* Result card */}
      <AnimatePresence>
        {isAnswered && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              background: isCorrect ? "#EAF3DE" : "#FCEBEB",
              border: `1.5px dashed ${isCorrect ? "#66BB6A" : "#FF6B6B"}`,
              borderRadius: 4, padding: 12, marginTop: 6,
              fontSize: 14, lineHeight: 1.6, fontFamily: "'Inter', sans-serif",
              color: "#1A1A2E",
            }}>
              {isCorrect
                ? <><span style={{ fontWeight: 600 }}>🎉 </span>{check.celebrate}</>
                : <><span style={{ fontWeight: 600 }}>💪 </span>{check.reteach}</>
              }
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Next Topic Suggestion Card ─────────────────────────────────
const NextTopicCard = ({ suggestion, onSendMessage, onGenerateStory }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    style={{
      background: "#EAF3DE", border: "1.5px dashed #66BB6A",
      borderLeft: "3px solid #66BB6A", borderRadius: 4,
      padding: 14, marginBottom: 8,
    }}
  >
    <p style={{
      fontFamily: "'Patrick Hand', cursive", fontSize: 15, color: "#2E7D32",
      margin: "0 0 6px", display: "flex", alignItems: "center", gap: 6,
    }}>
      <span style={{ fontSize: 18 }}>🎉</span> Topic Mastered!
    </p>
    <p style={{
      fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#1A1A2E",
      margin: "0 0 4px", fontWeight: 600,
    }}>
      {suggestion.mastered}
    </p>
    {suggestion.connection && (
      <p style={{
        fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#555",
        margin: "0 0 12px", lineHeight: 1.5,
      }}>
        {suggestion.connection}
      </p>
    )}
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button
        onClick={() => onSendMessage(`Tell me about ${suggestion.next}`)}
        style={{
          background: "#66BB6A", color: "#fff", border: "none", borderRadius: 14,
          padding: "8px 14px", cursor: "pointer", fontSize: 13,
          fontFamily: "'Patrick Hand', cursive",
        }}
      >
        Explore: {suggestion.next}
      </button>
      <button
        onClick={() => onGenerateStory?.(suggestion.mastered)}
        style={{
          border: "1.5px dashed #EF9F27", borderRadius: 14, background: "transparent",
          padding: "8px 14px", cursor: "pointer", fontSize: 13, color: "#E65100",
          fontFamily: "'Patrick Hand', cursive",
        }}
      >
        🎧 Story on {suggestion.mastered}
      </button>
      <button
        onClick={() => onSendMessage("I have a different doubt")}
        style={{
          border: "1.5px dashed #AAAAAA", borderRadius: 14, background: "transparent",
          padding: "8px 14px", cursor: "pointer", fontSize: 13, color: "#555",
          fontFamily: "'Patrick Hand', cursive",
        }}
      >
        Ask something else
      </button>
    </div>
  </motion.div>
);

// ─── Homework Report Card ───────────────────────────────────────
const HomeworkReportCard = ({ report, onGenerateStory }) => {
  const total = parseInt(report.total) || 0;
  const correct = parseInt(report.correct) || 0;
  const needsReview = report.needs_review || "—";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: "#FFF3E0", border: "1.5px dashed #EF9F27",
        borderLeft: "3px solid #EF9F27", borderRadius: 4,
        padding: 14, marginBottom: 8,
      }}
    >
      <p style={{
        fontFamily: "'Patrick Hand', cursive", fontSize: 15, color: "#E65100",
        margin: "0 0 10px", display: "flex", alignItems: "center", gap: 6,
      }}>
        <span style={{ fontSize: 18 }}>📋</span> Homework Report
      </p>
      <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
        <div style={{
          background: "#E8F5E9", borderRadius: 8, padding: "8px 14px", textAlign: "center", flex: 1,
        }}>
          <p style={{ fontFamily: "'Patrick Hand', cursive", fontSize: 22, color: "#2E7D32", margin: 0 }}>
            {correct}/{total}
          </p>
          <p style={{ fontSize: 11, color: "#555", margin: 0, fontFamily: "'Inter', sans-serif" }}>Correct</p>
        </div>
        <div style={{
          background: "#FFEBEE", borderRadius: 8, padding: "8px 14px", textAlign: "center", flex: 1,
        }}>
          <p style={{ fontFamily: "'Patrick Hand', cursive", fontSize: 22, color: "#C62828", margin: 0 }}>
            {total - correct}
          </p>
          <p style={{ fontSize: 11, color: "#555", margin: 0, fontFamily: "'Inter', sans-serif" }}>Need review</p>
        </div>
      </div>
      {report.needs_review && (
        <p style={{ fontSize: 13, color: "#555", margin: "0 0 4px", fontFamily: "'Inter', sans-serif" }}>
          <strong>Review:</strong> {needsReview}
        </p>
      )}
      {report.concept_gap && (
        <p style={{ fontSize: 13, color: "#555", margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
          <strong>Focus area:</strong> {report.concept_gap}
        </p>
      )}
      {report.story_recommendation && (
        <button
          onClick={() => onGenerateStory?.(report.story_recommendation)}
          style={{
            background: "#EF9F27", color: "#fff", border: "none", borderRadius: 14,
            padding: "8px 14px", cursor: "pointer", fontSize: 13,
            fontFamily: "'Patrick Hand', cursive",
          }}
        >
          🎧 Story on {report.story_recommendation}
        </button>
      )}
    </motion.div>
  );
};

// ─── Message Bubble ──────────────────────────────────────────────
const MessageBubble = ({ msg, buntyMode, onContinue }) => {
  const isUser = msg.role === "user";
  const isBunty = msg.role === "bunty";
  const isModel = msg.role === "model" || isBunty;

  let bubbleStyle = {};
  if (isUser) {
    bubbleStyle = {
      border: "1.5px dashed #448AFF",
      background: buntyMode ? "transparent" : "rgba(68,138,255,0.06)",
      borderRadius: "12px 0 12px 12px",
      opacity: buntyMode ? 0.7 : 1,
    };
  } else if (isBunty) {
    bubbleStyle = {
      border: "1.5px dashed #66BB6A",
      background: "rgba(102,187,106,0.06)",
      borderRadius: "0 12px 12px 12px",
    };
  } else {
    bubbleStyle = {
      border: "1.5px dashed #CCCCCC",
      background: "#FAFAFA",
      borderRadius: "0 12px 12px 12px",
    };
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        display: "flex", flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        marginBottom: 8,
      }}
    >
      {isModel && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          {isBunty ? <BuntyAvatar /> : <PriyaAvatar />}
          <span style={{ fontSize: 12, color: "#666666", fontFamily: "'Inter', sans-serif" }}>
            {isBunty ? "Bunty" : "Priya"}
          </span>
        </div>
      )}
      {isUser && buntyMode && (
        <span style={{ fontSize: 11, color: "#999", marginBottom: 2, fontFamily: "'Inter', sans-serif" }}>
          You whispered:
        </span>
      )}
      <div style={{
        ...bubbleStyle, padding: "10px 14px", maxWidth: "85%",
        fontSize: 14, lineHeight: 1.6, color: "#1A1A2E",
        fontFamily: "'Inter', sans-serif", whiteSpace: "pre-wrap",
      }}>
        <ReactMarkdown components={{
          p: ({children}) => <span>{children}</span>,
          strong: ({children}) => <strong style={{fontWeight: 600}}>{children}</strong>,
        }}>{msg.content}</ReactMarkdown>
      </div>
      {msg.role === "model" && msg.concept_check && (
        <div style={{ marginTop: 6, width: "100%", maxWidth: "85%" }}>
          <ConceptCheckCard check={msg.concept_check} onContinue={onContinue} />
        </div>
      )}
    </motion.div>
  );
};

// ─── Main Component ──────────────────────────────────────────────
export default function AskPriya({ user, language, board, classLevel, onGenerateStory, onAsked }) {
  const { getAccessToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [buntyMode, setBuntyMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [inputText, setInputText] = useState("");
  const [quickReplies, setQuickReplies] = useState([]);
  const [storyBridge, setStoryBridge] = useState(null);
  const [conceptCheck, setConceptCheck] = useState(null);
  const [nextTopicSuggestion, setNextTopicSuggestion] = useState(null);
  const [homeworkReport, setHomeworkReport] = useState(null);
  const [subject, setSubject] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const hasFiredAsked = useRef(false);

  // Auto-scroll on new messages
  useEffect(() => {
    const t = setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 150);
    return () => clearTimeout(t);
  }, [messages, loading, quickReplies, conceptCheck, storyBridge]);

  // Restore last session on mount
  useEffect(() => {
    const savedSessionId = localStorage.getItem("priya_session_id");
    if (!savedSessionId) return;

    const restoreSession = async () => {
      try {
        const token = getAccessToken() || "";
        const res = await fetch(`${BACKEND}/api/chat-sessions/${savedSessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          // Session expired, deleted, or forbidden — clear stale ID
          localStorage.removeItem("priya_session_id");
          return;
        }
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          setSessionId(data.session_id);
          setShowLanding(false);
          if (data.bunty_mode) setBuntyMode(true);
        }
      } catch (e) {
        console.warn("Failed to restore session:", e);
        localStorage.removeItem("priya_session_id");
      }
    };
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recentTopics = JSON.parse(localStorage.getItem("priya_recent_topics") || "[]");

  const sendMessage = useCallback(async (text, imageBase64 = null, options = {}) => {
    if (!text.trim() && !imageBase64) return;
    setLoading(true);
    setShowLanding(false);

    const userMsg = {
      role: "user",
      content: text || "Help me with this photo",
      timestamp: new Date().toISOString(),
      hidden: options.hidden === true,
    };
    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setQuickReplies([]);

    try {
      const token = getAccessToken() || "";

      const userName = user?.user_metadata?.full_name?.split(" ")[0]
        || user?.email?.split("@")[0]
        || "buddy";

      const payload = {
        message: text || "Please help me with this problem from my textbook.",
        session_id: sessionId,
        language: "en-IN",
        board: board || localStorage.getItem("you_board") || "CBSE",
        class_level: classLevel || localStorage.getItem("you_class") || "class_5_6",
        subject,
        bunty_mode: buntyMode,
        user_name: userName,
      };
      if (imageBase64) payload.image_base64 = imageBase64;

      const res = await fetch(`${BACKEND}/api/ask-priya`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      const modelMsg = {
        role: buntyMode ? "bunty" : "model",
        content: data.reply || "Hmm, I could not process that. Try again?",
        concept_check: data.concept_check || null,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, modelMsg]);
      if (!hasFiredAsked.current && onAsked) {
        hasFiredAsked.current = true;
        onAsked();
      }
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("priya_session_id", data.session_id);
      }
      setNextTopicSuggestion(data.next_topic_suggestion || null);
      setHomeworkReport(data.homework_report || null);
      setQuickReplies(data.concept_check ? [] : (data.quick_replies || []));
      setStoryBridge(data.story_bridge || null);

      // Save recent topic
      if (text && text.length > 5) {
        const recent = JSON.parse(localStorage.getItem("priya_recent_topics") || "[]");
        const updated = [text, ...recent.filter(t => t !== text)].slice(0, 5);
        localStorage.setItem("priya_recent_topics", JSON.stringify(updated));
      }
    } catch {
      setMessages(prev => [...prev, {
        role: "model",
        content: "Oops! Something went wrong. Try again?",
        timestamp: new Date().toISOString(),
      }]);
      setQuickReplies(["Try again", "Ask differently"]);
    }
    setLoading(false);
  }, [sessionId, language, board, classLevel, subject, buntyMode, onAsked]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = getAccessToken() || "";
      const res = await fetch(`${BACKEND}/api/chat-sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.warn("Failed to fetch history:", res.status);
        setHistoryList([]);
        return;
      }
      const data = await res.json();
      setHistoryList(data.sessions || []);
    } catch (e) {
      console.warn("Error fetching history:", e);
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  const closeHistory = () => {
    setHistoryOpen(false);
  };

  const startNewConversation = () => {
    localStorage.removeItem("priya_session_id");
    setMessages([]);
    setSessionId(null);
    setShowLanding(true);
    setBuntyMode(false);
    setHistoryOpen(false);
  };

  const resumeConversation = async (sid) => {
    setHistoryLoading(true);
    try {
      const token = getAccessToken() || "";
      const res = await fetch(`${BACKEND}/api/chat-sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.warn("Failed to load session:", res.status);
        return;
      }
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages);
        setSessionId(data.session_id);
        setShowLanding(false);
        setBuntyMode(!!data.bunty_mode);
        localStorage.setItem("priya_session_id", data.session_id);
      }
      setHistoryOpen(false);
    } catch (e) {
      console.warn("Error loading session:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      sendMessage("Help me with this problem:", base64);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, [sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowLanding(true);
    setQuickReplies([]);
    setStoryBridge(null);
    setConceptCheck(null);
    setNextTopicSuggestion(null);
    setHomeworkReport(null);
    setSubject(null);
  };

  // ─── Landing Screen ────────────────────────────────────────────
  if (showLanding) {
    return (
      <div className="askpriya-container" style={{ display: "flex", flexDirection: "column", height: "calc(100dvh - 48px - 64px - env(safe-area-inset-bottom, 0px))", maxWidth: 720, margin: "0 auto", width: "100%" }}>
        <div className="askpriya-content-area" style={{ flex: 1, overflowY: "auto", padding: "16px", minHeight: 0 }}>
        {/* Doodle decoration */}
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: 0, right: 0, pointerEvents: "none" }}>
            <DoodleLightbulb color="#FFD740" opacity={0.2} size={14} />
          </div>
        </div>

        {/* Greeting */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "flex-start" }}>
          <PriyaAvatar />
          <div style={{
            border: "1.5px dashed #CCCCCC", background: "#FAFAFA",
            borderRadius: "0 12px 12px 12px", padding: "10px 14px",
            fontSize: 14, lineHeight: 1.6, color: "#1A1A2E",
            fontFamily: "'Inter', sans-serif", flex: 1,
          }}>
            Hey! I am Priya, your study akka. What do you need help with?
          </div>
        </div>

        {/* Quick actions 2x2 grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          <ActionCard
            icon={<span style={{ fontSize: 18 }}>📐</span>}
            label="Solve a sum"
            accentColor="#448AFF"
            onClick={() => { setSubject("Mathematics"); sendMessage("I need help solving a math problem!"); }}
          />
          <ActionCard
            icon={<Lightbulb size={18} />}
            label="Explain a concept"
            accentColor="#FF6B6B"
            onClick={() => sendMessage("I want to learn something new!")}
          />
          <ActionCard
            icon={<Camera size={18} />}
            label="Snap homework"
            accentColor="#66BB6A"
            onClick={() => fileInputRef.current?.click()}
          />
          <ActionCard
            icon={<PenTool size={18} />}
            label="Check my English"
            accentColor="#AB47BC"
            onClick={() => { setSubject("English"); sendMessage("Help me with English"); }}
          />
        </div>

        {/* Recent topics */}
        {recentTopics.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: "#999", marginBottom: 6, fontFamily: "'Inter', sans-serif" }}>
              Recent
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {recentTopics.slice(0, 4).map((t, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(t)}
                  className="doodle-pill-inactive"
                  style={{ fontSize: 12, padding: "4px 10px", minHeight: 28 }}
                >
                  {t.length > 30 ? t.slice(0, 30) + "..." : t}
                </button>
              ))}
            </div>
          </div>
        )}

        </div>

        {/* Input bar */}
        <div className="askpriya-input-bar" style={{ flexShrink: 0 }}>
          <div>
            <button
              className="camera-btn"
              onClick={() => fileInputRef.current?.click()}
              style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}
              aria-label="Upload photo"
            >
              <Camera size={18} color="#999" />
            </button>
            <input
              type="text"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Priya anything..."
            />
            <button
              onClick={() => sendMessage(inputText)}
              disabled={!inputText.trim()}
              style={{
                width: 44, height: 44, borderRadius: "50%",
                background: inputText.trim() ? "#448AFF" : "rgba(68,138,255,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.2s",
              }}
              aria-label="Send message"
            >
              <Send size={18} color="#fff" />
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
          onChange={handlePhotoUpload} style={{ display: "none" }} />
      </div>
    );
  }

  // ─── Chat Screen ───────────────────────────────────────────────
  return (
    <div className="askpriya-container" style={{ display: "flex", flexDirection: "column", height: "calc(100dvh - 48px - 64px - env(safe-area-inset-bottom, 0px))", maxWidth: 720, margin: "0 auto", width: "100%", position: "relative" }}>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
        borderBottom: "1.5px dashed #DDDDDD",
        background: buntyMode ? "#FFFDE7" : "#FFFEF9",
        transition: "background 0.3s",
      }}>
        <button onClick={startNewChat} style={{
          background: "transparent", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", padding: 4,
        }} aria-label="Back to landing">
          <ArrowLeft size={20} color="#1A1A2E" />
        </button>
        <PriyaAvatar />
        <div style={{ flex: 1 }}>
          <span style={{ fontFamily: "'Patrick Hand', cursive", fontSize: 16, color: "#1A1A2E" }}>
            Priya
          </span>
          {subject && (
            <span style={{ fontSize: 12, color: "#666", marginLeft: 6, fontFamily: "'Inter', sans-serif" }}>
              {subject}
            </span>
          )}
        </div>
        {/* History pill */}
        <button
          onClick={openHistory}
          aria-label="View past conversations"
          style={{
            fontSize: 11,
            padding: "4px 8px",
            minHeight: 26,
            marginRight: 6,
            borderRadius: 12,
            border: "1px dashed #c7c7c7",
            background: "#fff",
            color: "#444",
            fontFamily: "'Inter', sans-serif",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          📋 History
        </button>
        {/* Bunty toggle */}
        <button
          onClick={() => setBuntyMode(b => !b)}
          className={buntyMode ? "doodle-pill-active" : "doodle-pill-inactive"}
          style={{ fontSize: 12, padding: "4px 10px", minHeight: 28, display: "flex", alignItems: "center", gap: 4 }}
        >
          <span style={{ fontSize: 14 }}>🐒</span> Bunty
        </button>
      </div>

      {/* Bunty mode banner */}
      <AnimatePresence>
        {buntyMode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{
              background: "rgba(255,215,64,0.08)", borderLeft: "3px solid #FFD740",
              padding: "8px 12px", fontSize: 12, color: "#7A5800",
              fontFamily: "'Inter', sans-serif", overflow: "hidden",
            }}
          >
            Bunty is asking for you! Watch him learn from Priya akka.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div className="askpriya-content-area" style={{
        flex: 1, overflowY: "auto", padding: "12px 16px", minHeight: 0,
        display: "flex", flexDirection: "column",
      }}>
        {/* Doodle decoration */}
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: 0, right: 0, pointerEvents: "none" }}>
            <DoodleMagnify color="#448AFF" opacity={0.1} size={12} />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {messages.filter(m => !m.hidden).map((msg, i) => (
            <MessageBubble
              key={i}
              msg={msg}
              buntyMode={buntyMode}
              onContinue={sendMessage}
            />
          ))}
        </AnimatePresence>

        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <PriyaAvatar />
            <TypingDots />
          </div>
        )}

        {/* Next topic suggestion */}
        {!loading && nextTopicSuggestion && (
          <NextTopicCard
            suggestion={nextTopicSuggestion}
            onSendMessage={sendMessage}
            onGenerateStory={onGenerateStory}
          />
        )}

        {/* Homework report */}
        {!loading && homeworkReport && (
          <HomeworkReportCard
            report={homeworkReport}
            onGenerateStory={onGenerateStory}
          />
        )}

        {/* Quick replies */}
        {!loading && quickReplies.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="quick-replies-scroll"
            style={{ display: "flex", gap: 6, overflowX: "auto", whiteSpace: "nowrap", marginBottom: 8, paddingBottom: 4, WebkitOverflowScrolling: "touch", scrollbarWidth: "none", flexShrink: 0 }}
          >
            {quickReplies.map((qr, i) => (
              <button
                key={i}
                onClick={() => sendMessage(qr)}
                style={{
                  border: "1.5px dashed #448AFF", borderRadius: 14,
                  padding: "6px 12px", background: "transparent", cursor: "pointer",
                  fontSize: 13, fontFamily: "'Patrick Hand', cursive", color: "#448AFF",
                  display: "inline-flex", flexShrink: 0, whiteSpace: "nowrap",
                }}
              >
                {qr}
              </button>
            ))}
          </motion.div>
        )}

        {/* Story bridge */}
        {!loading && storyBridge && !storyBridge.toLowerCase().includes("want to learn") && !storyBridge.toLowerCase().includes("something new") && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: "#FFF3E0", border: "1.5px dashed #EF9F27",
              borderRadius: 12, padding: 12, marginBottom: 8, cursor: "pointer",
            }}
            onClick={() => {
              const topic = typeof storyBridge === "string"
                ? storyBridge.replace(/^Want to hear.*on\s*/, "").replace(/\?$/, "")
                : storyBridge.topic || "";
              onGenerateStory?.(topic);
            }}
          >
            <p style={{ fontFamily: "'Patrick Hand', cursive", fontSize: 14, color: "#E65100", margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 16 }}>🎧</span>
              {typeof storyBridge === "string" ? storyBridge : `Want to hear the full story on "${storyBridge.topic}"?`}
            </p>
            <p style={{ fontSize: 12, color: "#BF360C", margin: "4px 0 0", fontFamily: "'Inter', sans-serif" }}>
              Tap to generate an audio story
            </p>
          </motion.div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input bar */}
      <div className="askpriya-input-bar" style={{ flexShrink: 0 }}>
        <div>
          <button
            className="camera-btn"
            onClick={() => fileInputRef.current?.click()}
            style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}
            aria-label="Upload photo"
          >
            <Camera size={18} color="#999" />
          </button>
          <input
            type="text"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={buntyMode ? "Whisper to Bunty..." : "Ask Priya anything..."}
          />
          <button
            onClick={() => sendMessage(inputText)}
            disabled={!inputText.trim() || loading}
            style={{
              width: 44, height: 44, borderRadius: "50%",
              background: inputText.trim() && !loading ? "#448AFF" : "rgba(68,138,255,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.2s",
            }}
            aria-label="Send message"
          >
            <Send size={18} color="#fff" />
          </button>
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
        onChange={handlePhotoUpload} style={{ display: "none" }} />

      {historyOpen && (
        <div
          onClick={closeHistory}
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 50,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "78%",
              height: "100%",
              background: "rgba(255,255,255,0.78)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              borderLeft: "0.5px solid rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
              animation: "slideInRight 0.22s ease-out",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px dashed rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>Past conversations</span>
              <span
                onClick={closeHistory}
                style={{ fontSize: 18, color: "#888", cursor: "pointer", padding: "0 4px" }}
              >
                ×
              </span>
            </div>

            <div style={{ padding: "10px 12px", borderBottom: "1px dashed rgba(0,0,0,0.1)" }}>
              <button
                onClick={startNewConversation}
                style={{
                  width: "100%",
                  background: "#378ADD",
                  color: "#fff",
                  padding: "8px",
                  borderRadius: 10,
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 500,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                + New conversation
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
              {historyLoading && (
                <div style={{ padding: "16px", textAlign: "center", fontSize: 11, color: "#888" }}>
                  Loading…
                </div>
              )}
              {!historyLoading && historyList.length === 0 && (
                <div style={{ padding: "16px", textAlign: "center", fontSize: 11, color: "#888" }}>
                  No past conversations yet.
                </div>
              )}
              {!historyLoading &&
                historyList.map((s) => {
                  const isCurrent = s.session_id === sessionId;
                  return (
                    <div
                      key={s.session_id}
                      onClick={() => resumeConversation(s.session_id)}
                      style={{
                        padding: "8px 10px",
                        marginBottom: 4,
                        borderRadius: 8,
                        border: isCurrent ? "0.5px solid #c7c7c7" : "0.5px solid rgba(0,0,0,0.1)",
                        background: isCurrent ? "rgba(255,244,238,0.85)" : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#222",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {s.preview || "New conversation"}
                      </div>
                      <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>
                        {s.message_count} messages
                        {s.bunty_mode && " · 🐿️ Bunty"}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
